// super-excalidraw: media library panel — same behaviour as the original
// MediaLibraryDialog but without the Dialog wrapper, so it fits inside a
// right-sidebar tab. Browse, upload, edit description, insert into canvas.

import React, { useEffect, useRef, useState } from "react";

import {
  fetchMediaLibrary,
  uploadMedia,
  patchMedia,
  deleteMedia,
  mediaUrl,
  type MediaItemDTO,
  type MediaLibraryDTO,
} from "../data/media";

import "./MediaLibraryPanel.scss";

type Props = {
  onInsert?: (item: MediaItemDTO) => void;
  /** Whether the parent sidebar/dialog is currently visible. We refresh on
   *  mount; pass `visible` if you also want to refresh when the tab is
   *  re-opened after being hidden. */
  visible?: boolean;
};

export const MediaLibraryPanel: React.FC<Props> = ({ onInsert, visible }) => {
  const [lib, setLib] = useState<MediaLibraryDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<
    Record<string, { name: string; description: string }>
  >({});
  const [uploading, setUploading] = useState(false);
  const [uploadName, setUploadName] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refresh = async () => {
    setError(null);
    try {
      const data = await fetchMediaLibrary();
      data.items.sort((a, b) => b.addedAt - a.addedAt);
      setLib(data);
      const ed: typeof editing = {};
      data.items.forEach((it) => {
        ed[it.id] = { name: it.name ?? "", description: it.description ?? "" };
      });
      setEditing(ed);
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setLib({ items: [] });
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // Refresh when the tab becomes visible again (cheap; the list is small).
  useEffect(() => {
    if (visible) {
      refresh();
    }
  }, [visible]);

  const onPickFile = () => fileInputRef.current?.click();

  const onFileSelected: React.ChangeEventHandler<HTMLInputElement> = async (
    e,
  ) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    if (!uploadName.trim()) {
      setUploadName(file.name.replace(/\.[^/.]+$/, ""));
    }
    setUploading(true);
    try {
      await uploadMedia({
        file,
        name: uploadName.trim() || file.name,
        description: uploadDescription.trim(),
      });
      setUploadName("");
      setUploadDescription("");
      await refresh();
    } catch (err: any) {
      setError(err?.message ?? String(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const onSave = async (item: MediaItemDTO) => {
    const draft = editing[item.id];
    if (!draft) {
      return;
    }
    setBusyId(item.id);
    try {
      await patchMedia(item.id, {
        name: draft.name,
        description: draft.description,
      });
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (item: MediaItemDTO) => {
    if (!confirm(`Remove "${item.name || item.id}" from the library?`)) {
      return;
    }
    setBusyId(item.id);
    try {
      await deleteMedia(item.id);
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="media-lib media-lib--panel">
      <p className="media-lib__intro">
        Server-synced media. Add a description so the AI knows when to insert
        each image. Click a card to insert it into the current diagram.
      </p>

      <div className="media-lib__upload">
        <input
          type="text"
          placeholder="Name (e.g. Company logo)"
          value={uploadName}
          onChange={(e) => setUploadName(e.target.value)}
        />
        <input
          type="text"
          placeholder="Description for AI (e.g. 4 Step AI logo, blue/white wordmark)"
          value={uploadDescription}
          onChange={(e) => setUploadDescription(e.target.value)}
        />
        <input
          type="file"
          ref={fileInputRef}
          onChange={onFileSelected}
          style={{ display: "none" }}
          accept="image/*"
        />
        <button type="button" disabled={uploading} onClick={onPickFile}>
          {uploading ? "Uploading…" : "Upload image"}
        </button>
      </div>

      {error && <div className="media-lib__error">Error: {error}</div>}
      {lib === null && <div className="media-lib__loading">Loading…</div>}
      {lib?.items.length === 0 && (
        <div className="media-lib__empty">
          No media yet. Upload your first image above.
        </div>
      )}

      <ul className="media-lib__grid">
        {lib?.items.map((item) => {
          const draft = editing[item.id] ?? { name: "", description: "" };
          return (
            <li key={item.id} className="media-lib__card">
              <div
                className="media-lib__thumb"
                onClick={() => onInsert?.(item)}
                role="button"
                title={onInsert ? "Insert into diagram" : "Preview"}
              >
                {item.mimeType?.startsWith("image/") ? (
                  <img
                    src={mediaUrl(item)}
                    alt={item.name || item.id}
                    loading="lazy"
                  />
                ) : (
                  <div className="media-lib__thumb-placeholder">
                    {item.mimeType || "file"}
                  </div>
                )}
              </div>
              <div className="media-lib__fields">
                <input
                  className="media-lib__name"
                  placeholder="Name"
                  value={draft.name}
                  onChange={(e) =>
                    setEditing((d) => ({
                      ...d,
                      [item.id]: { ...draft, name: e.target.value },
                    }))
                  }
                />
                <textarea
                  className="media-lib__desc"
                  placeholder="Description for AI"
                  rows={2}
                  value={draft.description}
                  onChange={(e) =>
                    setEditing((d) => ({
                      ...d,
                      [item.id]: { ...draft, description: e.target.value },
                    }))
                  }
                />
              </div>
              <div className="media-lib__actions">
                <button
                  type="button"
                  disabled={busyId === item.id}
                  onClick={() => onInsert?.(item)}
                  title="Insert into the current diagram"
                >
                  Insert
                </button>
                <button
                  type="button"
                  disabled={busyId === item.id}
                  onClick={() => onSave(item)}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="media-lib__danger"
                  disabled={busyId === item.id}
                  onClick={() => onDelete(item)}
                >
                  Delete
                </button>
              </div>
              <div className="media-lib__date">
                {new Date(item.addedAt * 1000).toLocaleString()}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
